import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSpinner,
    faChevronDown,
    faChevronUp,
    faClock,
    faHourglassHalf,
    faBolt,
    faCheckCircle,
    faLink,
    faTag,
    faCog,
    faUser,
    faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';

const formatDateTime = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const calcularDiferencia = (fechaInicioStr, fechaFinStr) => {
    if (!fechaInicioStr || !fechaFinStr) return null;
    const inicio = new Date(fechaInicioStr);
    const fin = new Date(fechaFinStr);
    if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return null;
    const diffMs = fin.getTime() - inicio.getTime();
    if (diffMs < 0) return null;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const remMins = diffMins % 60;
    if (hours < 24) {
        return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
};

const extraerTicketCombinado = item => {
    if (item.idTicketDestino) return item.idTicketDestino;
    if (item.idTicketPrincipal) return item.idTicketPrincipal;
    if (item.idTicketCombinado) return item.idTicketCombinado;
    return null;
};

const HistorialTickets = ({ idClienteAkasia }) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token } = useToken();
    const [tickets, setTickets] = useState([]);
    const [cargando, setCargando] = useState(false);

    // Estados para despliegue de movimientos / eventos del ticket
    const [expandedTicketId, setExpandedTicketId] = useState(null);
    const [ticketEventsMap, setTicketEventsMap] = useState({});
    const [isLoadingEventsMap, setIsLoadingEventsMap] = useState({});
    const [ticketEtiquetasMap, setTicketEtiquetasMap] = useState({});

    // Cargar etiquetas/incidencias asignadas al ticket
    const fetchTicketEtiquetas = async idTicket => {
        if (!idTicket || !token) return;
        try {
            const response = await fetch(`/api/SupportEngine/GetTicketEtiquetas?idTicket=${idTicket}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                setTicketEtiquetasMap(prev => ({ ...prev, [idTicket]: Array.isArray(list) ? list : [] }));
            }
        } catch {
            // Silencioso
        }
    };

    useEffect(() => {
        const fetchHistorialTickets = async () => {
            if (!idClienteAkasia) {
                setTickets([]);
                return;
            }

            setCargando(true);
            try {
                const response = await fetch(`/api/cliente/ObtenerHistorialTickets?idClienteAkasia=${idClienteAkasia}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1) {
                        const parsed = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles;
                        const lista = parsed.table2 || parsed.Table1 || [];
                        const validList = Array.isArray(lista) ? lista : [];
                        setTickets(validList);
                        validList.forEach(t => {
                            if (t.idTicket) fetchTicketEtiquetas(t.idTicket);
                        });
                    } else {
                        setTickets([]);
                    }
                } else {
                    setTickets([]);
                }
            } catch (error) {
                console.error('Error al obtener historial de tickets:', error);
                setTickets([]);
            } finally {
                setCargando(false);
            }
        };

        fetchHistorialTickets();
    }, [idClienteAkasia, token]);

    // Cargar eventos del ticket desde la API
    const fetchTicketEvents = async idTicket => {
        if (!idTicket || !token) return;
        setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: true }));
        try {
            const response = await fetch(`/api/SupportEngine/GetTicketEvents?idTicket=${idTicket}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                setTicketEventsMap(prev => ({ ...prev, [idTicket]: Array.isArray(list) ? list : [] }));
            } else {
                setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
            }
        } catch (err) {
            console.error('Error al cargar eventos del ticket:', err);
            setTicketEventsMap(prev => ({ ...prev, [idTicket]: [] }));
        } finally {
            setIsLoadingEventsMap(prev => ({ ...prev, [idTicket]: false }));
        }
    };

    const handleToggleTicketDetails = idTicket => {
        if (expandedTicketId === idTicket) {
            setExpandedTicketId(null);
            return;
        }

        setExpandedTicketId(idTicket);
        if (!ticketEventsMap[idTicket]) {
            fetchTicketEvents(idTicket);
        }
        if (!ticketEtiquetasMap[idTicket]) {
            fetchTicketEtiquetas(idTicket);
        }
    };

    const getNivelSoporteTexto = nivel => {
        switch (nivel) {
            case 1:
                return 'Nivel 1 – Básico';
            case 2:
                return 'Nivel 2 – Intermedio';
            case 3:
                return 'Nivel 3 – Avanzado';
            case 4:
                return 'Nivel 4 – Especialista';
            case 5:
                return 'Nivel 5 – Cotización';
            default:
                return nivel ? `Nivel ${nivel}` : 'Nivel 1 – Básico';
        }
    };

    const getEstadoConfig = (iEstadoTicket, vEstadoDesc) => {
        const estadoNum = Number(iEstadoTicket);
        switch (estadoNum) {
            case 1:
                return {
                    texto: vEstadoDesc || 'Abierto',
                    borderColor: '#2563eb',
                    nodeColor: '#3b82f6',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#eff6ff',
                        color: isDark ? '#60a5fa' : '#1d4ed8',
                        border: isDark ? '1px solid rgba(37, 99, 235, 0.4)' : '1px solid #bfdbfe'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : '#dbeafe',
                        color: isDark ? '#60a5fa' : '#1e40af',
                        border: isDark ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid #bfdbfe'
                    }
                };
            case 3:
                return {
                    texto: vEstadoDesc || 'En progreso',
                    borderColor: '#d97706',
                    nodeColor: '#f59e0b',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#fffbeb',
                        color: isDark ? '#fbbf24' : '#b45309',
                        border: isDark ? '1px solid rgba(217, 119, 6, 0.4)' : '1px solid #fde68a'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7',
                        color: isDark ? '#fbbf24' : '#8c531b',
                        border: isDark ? '1px solid rgba(245, 158, 11, 0.35)' : '1px solid #d8b965'
                    }
                };
            case 4:
                return {
                    texto: vEstadoDesc || 'Combinado',
                    borderColor: '#a855f7',
                    nodeColor: '#a855f7',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                        color: isDark ? '#c084fc' : '#7e22ce',
                        border: isDark ? '1px solid rgba(168, 85, 247, 0.4)' : '1px solid #d8b4fe'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(168, 85, 247, 0.18)' : '#f3e8ff',
                        color: isDark ? '#c084fc' : '#7e22ce',
                        border: isDark ? '1px solid rgba(168, 85, 247, 0.35)' : '1px solid #d8b4fe'
                    }
                };
            case 2:
            default:
                return {
                    texto: vEstadoDesc || 'Resuelto',
                    borderColor: '#059669',
                    nodeColor: '#10b981',
                    ticketBadgeStyle: {
                        backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#ecfdf5',
                        color: isDark ? '#34d399' : '#047857',
                        border: isDark ? '1px solid rgba(5, 150, 105, 0.4)' : '1px solid #a7f3d0'
                    },
                    badgeStyle: {
                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.18)' : '#d1fae5',
                        color: isDark ? '#34d399' : '#065f46',
                        border: isDark ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid #a7f3d0'
                    }
                };
        }
    };

    return (
        <Card
            className="shadow-sm border-0 h-100"
            style={{
                borderRadius: '12px',
                backgroundColor: isDark ? '#0b1727' : '#ffffff'
            }}
        >
            <Card.Header
                className="border-bottom py-3 d-flex justify-content-between align-items-center"
                style={{
                    backgroundColor: isDark ? '#0e1d30' : '#f9fafd',
                    borderColor: isDark ? '#233142' : '#edf2f9',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                }}
            >
                <h5 className="mb-0 fw-bold" style={{ color: isDark ? '#f9fafd' : '#0b1727' }}>
                    Historial de Tickets
                </h5>
                {cargando ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                ) : (
                    <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            fontSize: '0.8rem'
                        }}
                    >
                        {tickets.length} {tickets.length === 1 ? 'Ticket' : 'Tickets'}
                    </span>
                )}
            </Card.Header>

            <Card.Body className="p-3">
                {cargando ? (
                    <div className="text-center py-4 text-muted">
                        <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                        <small>Cargando historial de tickets...</small>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <small>No hay tickets registrados para este cliente.</small>
                    </div>
                ) : (
                    <div className="position-relative ps-4 pe-2 py-2">


                        {tickets.map((item, idx) => {
                            const isExpanded = expandedTicketId === item.idTicket;
                            const events = ticketEventsMap[item.idTicket] || [];
                            const isLoadingEvents = isLoadingEventsMap[item.idTicket];

                            // Combinación de tickets
                            const isCombinado = Boolean(item.bIsCombinado) || Number(item.iEstadoTicket) === 4 || item.vEstadoDesc?.toLowerCase() === 'combinado';
                            const ticketDestino = isCombinado ? extraerTicketCombinado(item) : null;
                            const estado = getEstadoConfig(isCombinado ? 4 : item.iEstadoTicket, isCombinado ? 'Combinado' : item.vEstadoDesc);

                            // Cálculos de tiempos
                            const tiempoEspera = item.dFechaAsignacion
                                ? calcularDiferencia(item.dCreatedDate, item.dFechaAsignacion)
                                : item.dFechaCierre
                                    ? calcularDiferencia(item.dCreatedDate, item.dFechaCierre)
                                    : calcularDiferencia(item.dCreatedDate, new Date());

                            const tiempoResolucion = item.dFechaCierre
                                ? calcularDiferencia(item.dFechaAsignacion, item.dFechaCierre)
                                : null;

                            const fechaHoraCierre = item.dFechaCierre ? formatDateTime(item.dFechaCierre) : null;

                            // Incidencias / Etiquetas
                            const incidenciasList = ticketEtiquetasMap[item.idTicket] || (item.vEtiquetas ? item.vEtiquetas.split(',').map(e => ({ vNombre: e.trim() })) : item.vIncidencia ? [{ vNombre: item.vIncidencia }] : []);

                            return (
                                <div key={`ticket-timeline-${item.idTicket || idx}`} className="position-relative mb-4">
                                    {/* Tarjeta del ticket */}
                                    <div
                                        className="p-3 p-md-3.5 border shadow-sm transition-all position-relative"
                                        style={{
                                            borderRadius: '12px',
                                            backgroundColor: isDark ? '#121e2d' : '#ffffff',
                                            borderColor: isDark ? '#233142' : '#e2e8f0',
                                            boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.25)' : '0 1px 3px rgba(0, 0, 0, 0.04)',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div
                                            className="cursor-pointer"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleToggleTicketDetails(item.idTicket)}
                                            title="Clic para ver/ocultar movimientos del ticket"
                                        >
                                            {/* Fila 1: Ticket # y Estado a la izq | Chevron a la der */}
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <a
                                                        href={`/support-engine/ticket/${item.idTicket}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={e => e.stopPropagation()}
                                                        className="rounded-pill fw-bold text-decoration-none d-inline-flex align-items-center"
                                                        style={{
                                                            fontSize: '0.82rem',
                                                            padding: '5px 14px',
                                                            gap: '7px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : '#eff6ff',
                                                            color: isDark ? '#38bdf8' : '#0284c7',
                                                            border: isDark ? '1px solid rgba(56, 189, 248, 0.35)' : '1px solid #bae6fd',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                        title={`Abrir ticket #${item.idTicket} en otra pantalla`}
                                                    >
                                                        <span>Ticket #{item.idTicket}</span>
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.65rem' }} />
                                                    </a>
                                                    <span
                                                        className="rounded-pill fw-semibold d-inline-flex align-items-center"
                                                        style={{
                                                            fontSize: '0.74rem',
                                                            padding: '4px 12px',
                                                            lineHeight: '1.2',
                                                            ...estado.badgeStyle
                                                        }}
                                                    >
                                                        {estado.texto}
                                                    </span>
                                                </div>

                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronUp : faChevronDown}
                                                    style={{ color: isDark ? '#94a3b8' : '#9ca3af', fontSize: '0.8rem' }}
                                                />
                                            </div>

                                            {/* Asunto si existe */}
                                            {item.vAsunto && (
                                                <div className="text-muted fw-normal mb-2.5" style={{ fontSize: '0.82rem' }}>
                                                    {item.vAsunto}
                                                </div>
                                            )}

                                            {/* Fila 2: Métricas del Ticket */}
                                            <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: '8px 10px' }}>
                                                {/* Tiempo de espera */}
                                                {tiempoEspera && (
                                                    <span
                                                        className="rounded-pill fw-medium d-inline-flex align-items-center text-nowrap"
                                                        style={{
                                                            fontSize: '0.76rem',
                                                            padding: '5px 14px',
                                                            gap: '7px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.16)' : '#f1f5f9',
                                                            color: isDark ? '#e2e8f0' : '#475569',
                                                            border: isDark ? '1px solid rgba(148, 163, 184, 0.35)' : '1px solid #cbd5e1'
                                                        }}
                                                        title="Tiempo en espera antes de ser atendido"
                                                    >
                                                        <FontAwesomeIcon icon={faHourglassHalf} className="text-muted" style={{ fontSize: '0.72rem' }} />
                                                        <span>Espera {tiempoEspera}</span>
                                                    </span>
                                                )}

                                                {/* Tiempo de resolución */}
                                                {tiempoResolucion && (
                                                    <span
                                                        className="rounded-pill fw-medium d-inline-flex align-items-center text-nowrap"
                                                        style={{
                                                            fontSize: '0.76rem',
                                                            padding: '5px 14px',
                                                            gap: '7px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.18)' : '#dcfce7',
                                                            color: isDark ? '#6ee7b7' : '#15803d',
                                                            border: isDark ? '1px solid rgba(52, 211, 153, 0.45)' : '1px solid #bbf7d0'
                                                        }}
                                                        title="Tiempo total que tomó resolver este ticket"
                                                    >
                                                        <FontAwesomeIcon icon={faBolt} style={{ fontSize: '0.72rem', color: isDark ? '#34d399' : '#16a34a' }} />
                                                        <span>Resolución {tiempoResolucion}</span>
                                                    </span>
                                                )}

                                                {/* Fecha y hora de cierre */}
                                                {fechaHoraCierre && (
                                                    <span
                                                        className="rounded-pill fw-medium d-inline-flex align-items-center text-nowrap"
                                                        style={{
                                                            fontSize: '0.76rem',
                                                            padding: '5px 14px',
                                                            gap: '7px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.18)' : '#fee2e2',
                                                            color: isDark ? '#fca5a5' : '#b91c1c',
                                                            border: isDark ? '1px solid rgba(248, 113, 113, 0.45)' : '1px solid #fecaca'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.72rem', color: isDark ? '#f87171' : '#dc2626' }} />
                                                        <span>Cerrado {fechaHoraCierre}</span>
                                                    </span>
                                                )}

                                                {/* Si el ticket se combinó */}
                                                {isCombinado && (
                                                    ticketDestino ? (
                                                        <a
                                                            href={`/support-engine/ticket/${ticketDestino}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={e => e.stopPropagation()}
                                                            className="rounded-pill fw-medium d-inline-flex align-items-center text-decoration-none text-nowrap"
                                                            style={{
                                                                fontSize: '0.76rem',
                                                                padding: '5px 14px',
                                                                gap: '7px',
                                                                lineHeight: '1.25',
                                                                backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                                                                color: isDark ? '#d8b4fe' : '#7e22ce',
                                                                border: isDark ? '1px solid rgba(192, 132, 252, 0.45)' : '1px solid #d8b4fe',
                                                                cursor: 'pointer'
                                                            }}
                                                            title={`Abrir ticket principal #${ticketDestino} en otra pantalla`}
                                                        >
                                                            <FontAwesomeIcon icon={faLink} style={{ fontSize: '0.72rem', color: isDark ? '#c084fc' : '#9333ea' }} />
                                                            <span>Combinado con Ticket #{ticketDestino}</span>
                                                        </a>
                                                    ) : (
                                                        <span
                                                            className="rounded-pill fw-medium d-inline-flex align-items-center text-nowrap"
                                                            style={{
                                                                fontSize: '0.76rem',
                                                                padding: '5px 14px',
                                                                gap: '7px',
                                                                lineHeight: '1.25',
                                                                backgroundColor: isDark ? 'rgba(168, 85, 247, 0.2)' : '#f3e8ff',
                                                                color: isDark ? '#d8b4fe' : '#7e22ce',
                                                                border: isDark ? '1px solid rgba(192, 132, 252, 0.45)' : '1px solid #d8b4fe'
                                                            }}
                                                            title="Este ticket fue combinado"
                                                        >
                                                            <FontAwesomeIcon icon={faLink} style={{ fontSize: '0.72rem', color: isDark ? '#c084fc' : '#9333ea' }} />
                                                            <span>Ticket Combinado</span>
                                                        </span>
                                                    )
                                                )}

                                                {/* Incidencia(s) */}
                                                {incidenciasList.length > 0 && incidenciasList.map((inc, i) => (
                                                    <span
                                                        key={`inc-${i}`}
                                                        className="rounded-pill fw-medium d-inline-flex align-items-center text-nowrap"
                                                        style={{
                                                            fontSize: '0.76rem',
                                                            padding: '5px 14px',
                                                            gap: '7px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : '#eff6ff',
                                                            color: isDark ? '#93c5fd' : '#1d4ed8',
                                                            border: isDark ? '1px solid rgba(96, 165, 250, 0.45)' : '1px solid #bfdbfe'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.72rem', color: isDark ? '#60a5fa' : '#2563eb' }} />
                                                        <span>Incidencia: {inc.vNombre || inc.vNombreEtiqueta || inc}</span>
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Fila 3: Nivel de soporte, Creado por y Fecha de creación */}
                                            <div
                                                className="d-flex align-items-center flex-wrap pt-3 mt-1 border-top"
                                                style={{
                                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
                                                    gap: '8px 10px'
                                                }}
                                            >
                                                <span
                                                    className="rounded-pill d-inline-flex align-items-center fw-medium text-nowrap"
                                                    style={{
                                                        padding: '4px 13px',
                                                        gap: '6px',
                                                        lineHeight: '1.25',
                                                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.14)' : '#fffbeb',
                                                        color: isDark ? '#fde047' : '#b45309',
                                                        border: isDark ? '1px solid rgba(251, 191, 36, 0.45)' : '1px solid #fde68a',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCog} style={{ fontSize: '0.70rem', color: isDark ? '#fbbf24' : '#d97706' }} />
                                                    <span>{getNivelSoporteTexto(item.idCatNivelSoporte)}</span>
                                                </span>

                                                {item.vCreatedUser && (
                                                    <span
                                                        className="rounded-pill d-inline-flex align-items-center fw-medium text-nowrap"
                                                        style={{
                                                            padding: '4px 13px',
                                                            gap: '6px',
                                                            lineHeight: '1.25',
                                                            backgroundColor: isDark ? 'rgba(148, 163, 184, 0.14)' : '#f1f5f9',
                                                            color: isDark ? '#e2e8f0' : '#334155',
                                                            border: isDark ? '1px solid rgba(148, 163, 184, 0.35)' : '1px solid #cbd5e1',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#475569' }} />
                                                        <span>{item.vCreatedUser}</span>
                                                    </span>
                                                )}

                                                <span className="d-inline-flex align-items-center text-nowrap ms-1" style={{ fontSize: '0.76rem', color: isDark ? '#94a3b8' : '#64748b', gap: '6px' }}>
                                                    <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.74rem' }} />
                                                    <span>{formatDateTime(item.dCreatedDate)}</span>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Panel desplegable con movimientos del ticket */}
                                        {isExpanded && (
                                            <div
                                                className="mt-3 p-3 border"
                                                style={{
                                                    borderRadius: '10px',
                                                    backgroundColor: isDark ? '#0e1d30' : '#f8fafc',
                                                    borderColor: isDark ? '#233142' : '#e2e8f0',
                                                    animation: 'fadeIn 0.2s ease-in'
                                                }}
                                            >
                                                {/* Fecha de cierre si existe */}
                                                {item.dFechaCierre && Number(item.iEstadoTicket) !== 1 && (
                                                    <div
                                                        className="d-flex justify-content-between pb-2 mb-2 border-bottom"
                                                        style={{
                                                            fontSize: '0.78rem',
                                                            borderColor: isDark ? '#233142' : '#e2e8f0',
                                                            color: isDark ? '#94a3b8' : '#64748b'
                                                        }}
                                                    >
                                                        <span className="fw-medium">Cerrado:</span>
                                                        <span style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{formatDateTime(item.dFechaCierre)}</span>
                                                    </div>
                                                )}

                                                {/* Título de movimientos */}
                                                <div
                                                    className="fw-semibold mb-2 d-flex align-items-center justify-content-between"
                                                    style={{
                                                        fontSize: '0.74rem',
                                                        letterSpacing: '0.04em',
                                                        textTransform: 'uppercase',
                                                        color: isDark ? '#94a3b8' : '#64748b'
                                                    }}
                                                >
                                                    <span>MOVIMIENTOS DEL TICKET</span>
                                                    {isLoadingEvents && <FontAwesomeIcon icon={faSpinner} spin className="text-primary" />}
                                                </div>

                                                {isLoadingEvents ? (
                                                    <div className="text-center py-3" style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2 text-primary" />
                                                        Cargando movimientos...
                                                    </div>
                                                ) : events.length === 0 ? (
                                                    <div className="text-center py-2 fst-italic" style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                        Sin movimientos registrados.
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="position-relative ps-3 my-2"
                                                        style={{ borderLeft: isDark ? '2px solid #233142' : '2px solid #cbd5e1' }}
                                                    >
                                                        {[...events]
                                                            .sort((a, b) => (b.idTicketHistorial || 0) - (a.idTicketHistorial || 0))
                                                            .map(ev => {
                                                                const isCierreEv = ev.vTipoEvento === 'CIERRE';
                                                                const isCreacionEv = ev.vTipoEvento === 'CREACION';
                                                                const isReaperturaEv = ev.vTipoEvento === 'REAPERTURA';
                                                                const isCombinadoEv = ev.vTipoEvento === 'COMBINACION_TICKETS';

                                                                const dotColor = isCierreEv
                                                                    ? '#ef4444'
                                                                    : isReaperturaEv
                                                                        ? '#f59e0b'
                                                                        : isCreacionEv
                                                                            ? '#10b981'
                                                                            : isCombinadoEv
                                                                                ? '#a855f7'
                                                                                : '#3b82f6';

                                                                return (
                                                                    <div key={ev.idTicketHistorial} className="mb-3 ps-2 position-relative">
                                                                        <div
                                                                            className="position-absolute rounded-circle"
                                                                            style={{
                                                                                left: '-20px',
                                                                                top: '5px',
                                                                                width: '8px',
                                                                                height: '8px',
                                                                                backgroundColor: dotColor
                                                                            }}
                                                                        />
                                                                        <div className="d-flex align-items-center justify-content-between">
                                                                            <span className="fw-semibold" style={{ fontSize: '0.8rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                                                                {ev.vTitulo}
                                                                            </span>
                                                                            <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                                                {formatDateTime(ev.dCreatedDate)}
                                                                            </span>
                                                                        </div>
                                                                        {ev.vDescripcion && (
                                                                            <div className="mt-1" style={{ fontSize: '0.76rem', color: isDark ? '#cbd5e1' : '#475569' }}>
                                                                                {ev.vDescripcion}
                                                                            </div>
                                                                        )}
                                                                        {ev.vCreatedUser && (
                                                                            <div className="mt-1" style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                                                Por: <span className="fw-medium" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{ev.vCreatedUser}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                    </div>
                                                )}

                                                <div className="mt-3 pt-2 border-top text-center" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                                                    <a
                                                        href={`/support-engine/ticket/${item.idTicket}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={e => e.stopPropagation()}
                                                        className="text-primary text-decoration-none fw-semibold d-inline-flex align-items-center gap-1"
                                                        style={{ fontSize: '0.78rem' }}
                                                        title={`Abrir ticket #${item.idTicket} en otra pantalla`}
                                                    >
                                                        <span>Abrir ticket #{item.idTicket} en otra pantalla</span>
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.7rem' }} />
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

HistorialTickets.propTypes = {
    idClienteAkasia: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default HistorialTickets;